import React from "react";
import { colors } from "../../lib/designTokens";
import { IntentRadarSeries } from "./localIntentRadarAdapter";
import { formatPercentage } from "../../lib/percentageFormatting";
import { formatRecommendationScoreOutOfTen } from "../../lib/localRecommendationStrength";
import { getRadarSeriesColor } from "../../lib/radarPalette";

type IntentRadarProps = {
  title: string;
  description: string;
  series: IntentRadarSeries[];
  status?: "ready" | "empty" | "error";
  errorMessage?: string;
};

const SIZE = { width: 720, height: 420, cx: 360, cy: 188, radius: 132 };
const CURVE_TENSION = 0.25;

type RadarPoint = { x: number; y: number };

function constrainControlPoint(point: RadarPoint, maxRadius: number): RadarPoint {
  const dx = point.x - SIZE.cx;
  const dy = point.y - SIZE.cy;
  const distance = Math.hypot(dx, dy);
  if (distance <= maxRadius || distance === 0) return point;

  const scale = maxRadius / distance;
  return { x: SIZE.cx + dx * scale, y: SIZE.cy + dy * scale };
}

/** Build a closed Catmull–Rom-style curve through every measured radar point. */
export function createClosedSmoothPath(points: RadarPoint[]): string {
  if (points.length < 2) return "";

  const coordinate = ({ x, y }: RadarPoint) => `${x},${y}`;
  const segments = points.map((start, index) => {
    const end = points[(index + 1) % points.length];
    const previous = points[(index - 1 + points.length) % points.length];
    const next = points[(index + 2) % points.length];
    const startRadius = Math.hypot(start.x - SIZE.cx, start.y - SIZE.cy);
    const endRadius = Math.hypot(end.x - SIZE.cx, end.y - SIZE.cy);
    const segmentRadius = Math.max(startRadius, endRadius, 0);
    const control1 = constrainControlPoint({
      x: start.x + (end.x - previous.x) * CURVE_TENSION,
      y: start.y + (end.y - previous.y) * CURVE_TENSION,
    }, segmentRadius);
    const control2 = constrainControlPoint({
      x: end.x - (next.x - start.x) * CURVE_TENSION,
      y: end.y - (next.y - start.y) * CURVE_TENSION,
    }, segmentRadius);

    return `C ${coordinate(control1)} ${coordinate(control2)} ${coordinate(end)}`;
  });

  return `M ${coordinate(points[0])} ${segments.join(" ")} Z`;
}

function pointFor(index: number, count: number, value: number, radius = SIZE.radius) {
  const angle = -Math.PI / 2 + (2 * Math.PI * index) / count;
  const clamped = Math.max(0, Math.min(100, value));
  const distance = (clamped / 100) * radius;
  return {
    x: SIZE.cx + Math.cos(angle) * distance,
    y: SIZE.cy + Math.sin(angle) * distance,
    labelX: SIZE.cx + Math.cos(angle) * (radius + 42),
    labelY: SIZE.cy + Math.sin(angle) * (radius + 42),
  };
}

function percent(value: number | null): string {
  return formatPercentage(value);
}

export default function IntentRadar({
  title,
  description,
  series,
  status = "ready",
  errorMessage,
}: IntentRadarProps) {
  const instanceId = React.useId();
  const axes = series[0]?.metrics ?? [];
  const loadingOrError = status === "error";
  let competitorIndex = 0;
  const coloredSeries = series.map((item) => {
    const color = item.kind === "customer"
      ? getRadarSeriesColor("customer")
      : getRadarSeriesColor("competitor", competitorIndex++);
    return { item, color };
  });

  return (
    <section aria-label={title} style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 18, background: "#fff", width: "100%", boxSizing: "border-box", minWidth: 0 }}>
      <h3 style={{ margin: 0, color: "#0f172a", fontSize: 17, fontWeight: 800 }}>{title}</h3>
      <p style={{ margin: "6px 0 14px", color: "#64748b", fontSize: 12 }}>{description}</p>
      {loadingOrError ? (
        <p role="alert" style={{ color: "#991b1b", margin: 0 }}>{errorMessage || "Radar data could not be loaded."}</p>
      ) : status === "empty" || axes.length === 0 ? (
        <p role="status" style={{ color: "#64748b", margin: 0 }}>No intent performance data is available for this run.</p>
      ) : (
        <>
          <p className="intent-radar-scroll-hint" style={{ margin: "0 0 6px", color: colors.textSecondary, fontSize: 11 }}>
            On narrow screens, scroll horizontally to view every chart axis and table column.
          </p>
          <div style={{ width: "100%", minWidth: 0, overflowX: "auto" }} data-testid="intent-radar-chart-wrap">
            <svg
              viewBox={`0 0 ${SIZE.width} ${SIZE.height}`}
              role="group"
              aria-labelledby={`${instanceId}-title ${instanceId}-description`}
              style={{ display: "block", width: "100%", minWidth: 620, height: "auto", overflow: "visible" }}
            >
              <title id={`${instanceId}-title`}>{title}, 0 to 100 percent scale</title>
              <desc id={`${instanceId}-description`}>{description} Each spoke uses the same zero to one hundred percent organic visibility scale.</desc>
              {[25, 50, 75, 100].map((tick) => <circle key={tick} cx={SIZE.cx} cy={SIZE.cy} r={(tick / 100) * SIZE.radius} fill="none" stroke={colors.border} strokeWidth="1" />)}
              {axes.map((metric, index) => {
                const outer = pointFor(index, axes.length, 100);
                return <g key={metric.axisKey}>
                  <line x1={SIZE.cx} y1={SIZE.cy} x2={outer.x} y2={outer.y} stroke={colors.border} strokeWidth="1" />
                  <text x={outer.labelX} y={outer.labelY} textAnchor={outer.labelX < SIZE.cx - 8 ? "end" : outer.labelX > SIZE.cx + 8 ? "start" : "middle"} dominantBaseline="middle" fill={colors.textPrimary} fontSize="13" fontWeight="700">{metric.axisLabel}</text>
                </g>;
              })}
              {[25, 50, 75, 100].map((tick) => <text key={`tick-${tick}`} x={SIZE.cx + 5} y={SIZE.cy - (tick / 100) * SIZE.radius - 3} fill={colors.textSecondary} fontSize="10">{tick}%</text>)}
              {coloredSeries.map(({ item, color }) => {
                const valueByKey = new Map(item.metrics.map((metric) => [metric.axisKey, metric]));
                const points = axes.map((axis, index) => {
                  const metric = valueByKey.get(axis.axisKey);
                  return metric?.value === null || metric?.value === undefined ? null : pointFor(index, axes.length, metric.value);
                });
                const complete = points.every(Boolean);
                const path = complete ? createClosedSmoothPath(points as RadarPoint[]) : "";
                return <g key={item.id}>
                  {path && <path d={path} fill={color} fillOpacity="0.14" stroke={color} strokeWidth={item.kind === "customer" ? 4 : 1.5} strokeLinejoin="round" />}
                  {points.map((point, index) => {
                    if (!point) return null;
                    const metric = valueByKey.get(axes[index].axisKey)!;
                    const tooltip = `${item.label}: ${axes[index].axisLabel}, ${percent(metric.value)} organic visibility; ${metric.numerator ?? "—"} of ${metric.denominator ?? "—"} completed organic responses.`;
                    return <circle className="intent-radar-point" key={`${item.id}-${axes[index].axisKey}`} cx={point.x} cy={point.y} r="6" fill={color} stroke="#fff" strokeWidth="2" tabIndex={0} role="img" aria-label={tooltip}>
                      <title>{tooltip}</title>
                    </circle>;
                  })}
                </g>;
              })}
            </svg>
          </div>
          <style>{`.intent-radar-point:focus-visible { stroke: ${colors.textPrimary}; stroke-width: 3; }`}</style>
          <div aria-label="Radar series" style={{ display: "flex", flexWrap: "wrap", gap: 12, margin: "4px 0 14px" }}>
            {coloredSeries.map(({ item, color }) => <span key={item.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#334155", fontSize: 12 }}><span aria-hidden="true" style={{ display: "inline-block", width: 10, height: 10, borderRadius: 999, background: color }} />{item.label}</span>)}
          </div>
          <div style={{ width: "100%", overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 680, fontSize: 12, textAlign: "left" }}>
              <caption style={{ textAlign: "left", fontWeight: 750, color: "#334155", paddingBottom: 8 }}>Organic visibility and response detail by Local intent</caption>
              <thead><tr>{["Intent group", "Visibility", "Mentions / eligible responses", "Recommendation strength", "Low-strength responses", "Unavailable responses"].map((label) => <th key={label} scope="col" style={{ padding: "8px 7px", borderBottom: "1px solid #cbd5e1", color: "#475569", fontWeight: 750 }}>{label}</th>)}</tr></thead>
              <tbody>{series.flatMap((item) => item.metrics.map((axis) => <tr key={`${item.id}-${axis.axisKey}`}>
                <th scope="row" title={`${item.label}: ${axis.axisLabel} organic visibility`} style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#0f172a", fontWeight: 700 }}>{axis.axisLabel}<span style={{ display: "block", marginTop: 3, color: "#64748b", fontWeight: 500 }}>{item.label}</span></th>
                <td style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{percent(axis.value)}</td>
                <td style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{axis.numerator ?? "—"} / {axis.denominator ?? "—"}</td>
                <td style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{formatRecommendationScoreOutOfTen(axis.recommendationStrength, "score_out_of_ten")}</td>
                <td style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{axis.lowStrengthCount ?? "—"}</td>
                <td style={{ padding: "9px 7px", borderBottom: "1px solid #eef2f7", color: "#475569", fontVariantNumeric: "tabular-nums" }}>{axis.errorCount ?? "—"}</td>
              </tr>))}</tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
